import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import apiClient from '@/lib/api/client';
import { LargeTitleHeader } from '@/components/common';
import { estimatePath, jobPath, quotePath } from '@/lib/utils/navPaths';
import { createQuoteFromEstimate } from '@/lib/api/quotes';

type EstimateItem = {
  product_id: number | null;
  product_name: string;
  quantity: number;
  unit_price: number;
  total: number;
  item_type?: 'item' | 'heading';
  heading_level?: 1 | 2;
  sort_order?: number;
};

type Estimate = {
  id: number;
  title: string;
  client_id: number;
  job_id: number;
  total_amount: number;
  status: string;
  created_at: string;
  items?: EstimateItem[];
  job_title?: string | null;
  client_name?: string | null;
  quote_id?: number | null;
  quote_number?: string | null;
};

export const EstimateDetailPage = () => {
  const { id, clientId, jobId } = useParams<{ id: string; clientId?: string; jobId?: string }>();
  const navigate = useNavigate();
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [editItems, setEditItems] = useState<EstimateItem[]>([]);
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    if (id) {
      loadEstimate();
    }
  }, [id]);

  // Upgrade the flat /estimates/:id URL to the canonical /clients/:c/jobs/:j/estimates/:e
  // once the estimate (and thus its client/job) is known, so the address bar mirrors the hierarchy.
  useEffect(() => {
    if (!id || !estimate?.client_id || !estimate.job_id) return;
    if (String(clientId) !== String(estimate.client_id) || String(jobId) !== String(estimate.job_id)) {
      navigate(estimatePath(estimate.client_id, estimate.job_id, id), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimate, clientId, jobId, id]);

  const loadEstimate = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/estimates/${id}`);
      setEstimate(response.data);
    } catch (err) {
      console.error('Failed to load estimate:', err);
      setError('Failed to load estimate');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = () => {
    if (estimate) {
      setEditTitle(estimate.title);
      setEditStatus(estimate.status);
      setEditItems(estimate.items ? [...estimate.items] : []);
      setIsEditing(true);
    }
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditTitle('');
    setEditStatus('');
    setEditItems([]);
  };

  const saveChanges = async () => {
    if (!estimate) return;
    
    try {
      setSaving(true);
      await apiClient.put(`/estimates/${id}`, {
        title: editTitle,
        status: editStatus,
        items: editItems,
      });
      await loadEstimate();
      setIsEditing(false);
    } catch (err) {
      console.error('Failed to save estimate:', err);
      setError('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this estimate? This action cannot be undone.')) {
      return;
    }
    
    try {
      setDeleting(true);
      await apiClient.delete(`/estimates/${id}`);
      // Land on the job, not history-back into the now-deleted record
      if (estimate?.job_id) navigate(jobPath(estimate.client_id, estimate.job_id));
      else navigate('/jobs');
    } catch (err) {
      console.error('Failed to delete estimate:', err);
      setError('Failed to delete estimate');
      setDeleting(false);
    }
  };

  const updateItem = (index: number, field: keyof EstimateItem, value: number | string) => {
    const newItems = [...editItems];
    if (field === 'quantity' || field === 'unit_price') {
      const numValue = Number(value) || 0;
      newItems[index] = { 
        ...newItems[index], 
        [field]: numValue,
        total: field === 'quantity' 
          ? numValue * newItems[index].unit_price 
          : newItems[index].quantity * numValue
      };
    } else {
      newItems[index] = { ...newItems[index], [field]: value };
    }
    setEditItems(newItems);
  };

  const removeItem = (index: number) => {
    setEditItems(editItems.filter((_, i) => i !== index));
  };

  const addItem = () => {
    const maxSort = editItems.reduce((m, i) => Math.max(m, i.sort_order ?? 0), 0);
    setEditItems([...editItems, {
      product_id: null,
      product_name: '',
      quantity: 1,
      unit_price: 0,
      total: 0,
      item_type: 'item',
      sort_order: maxSort + 1,
    }]);
  };

  // DECIMAL columns arrive as strings from the API — coerce before math (#96)
  const calculateTotal = () =>
    editItems.reduce((sum, item) => sum + (item.item_type === 'heading' ? 0 : Number(item.total) || 0), 0);

  const formatCurrency = (value: number | string) =>
    `$${(Number(value) || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // One-click estimate → quote (#96): converts this estimate and opens the
  // quote builder to review, adjust tax, and send.
  const convertToQuote = async () => {
    if (!estimate) return;
    setConverting(true);
    try {
      const r = await createQuoteFromEstimate([estimate.id], [], estimate.job_id);
      navigate(quotePath(estimate.client_id, estimate.job_id, r.id));
    } catch {
      setError(null);
      window.alert('Could not create the quote — try again.');
    } finally {
      setConverting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'declined':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="mt-2 text-gray-600">Loading estimate...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error || !estimate) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error || 'Estimate not found'}</p>
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90"
            >
              Go Back
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="flex flex-col min-h-screen">
        {/* iOS Large Title Header */}
        <LargeTitleHeader
          title={`EST-${estimate.id.toString().padStart(5, '0')}`}
          subtitle={estimate.title}
          onBack={() => {
            if (estimate.client_id && estimate.job_id) navigate(`/clients/${estimate.client_id}/jobs/${estimate.job_id}`);
            else navigate(-1);
          }}
          backLabel="Job"
          actions={
            <div className="flex items-center gap-2">
              {isEditing ? (
                <>
                  <button onClick={cancelEditing} disabled={saving} className="h-9 px-3 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Cancel</button>
                  <button onClick={saveChanges} disabled={saving} className="h-9 px-3 bg-primary text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
                </>
              ) : (
                <>
                  <span className={`hidden sm:inline px-2.5 py-1 text-xs font-medium rounded-full ${getStatusColor(estimate.status)}`}>
                    {estimate.status?.charAt(0).toUpperCase() + estimate.status?.slice(1)}
                  </span>
                  {estimate.quote_id ? (
                    <button
                      onClick={() => navigate(quotePath(estimate.client_id, estimate.job_id, estimate.quote_id!))}
                      className="flex items-center gap-1.5 h-9 px-3 bg-green-100 text-green-800 rounded-lg text-sm font-medium hover:bg-green-200"
                      title="This estimate has been converted — open its quote"
                    >
                      <span className="material-symbols-outlined text-base">request_quote</span>
                      <span className="hidden sm:inline">{estimate.quote_number || 'Quote'}</span>
                    </button>
                  ) : (
                    <button
                      onClick={convertToQuote}
                      disabled={converting}
                      className="flex items-center gap-1.5 h-9 px-3 bg-white border border-primary/40 text-primary rounded-lg text-sm font-medium hover:bg-primary/5 disabled:opacity-50"
                      title="Turn this estimate into a client-ready quote"
                    >
                      <span className={`material-symbols-outlined text-base ${converting ? 'animate-spin' : ''}`}>{converting ? 'progress_activity' : 'request_quote'}</span>
                      <span className="hidden sm:inline">{converting ? 'Converting…' : 'To Quote'}</span>
                    </button>
                  )}
                  <button onClick={() => window.print()} className="hidden sm:flex items-center gap-1.5 h-9 px-3 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">
                    <span className="material-symbols-outlined text-base">print</span>
                    Print
                  </button>
                  <button onClick={startEditing} className="flex items-center gap-1.5 h-9 px-3 bg-primary text-white rounded-lg text-sm font-medium hover:opacity-90">
                    <span className="material-symbols-outlined text-base">edit</span>
                    <span className="hidden sm:inline">Edit</span>
                  </button>
                  <button onClick={handleDelete} disabled={deleting} className="flex items-center gap-1.5 h-9 px-3 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50">
                    <span className="material-symbols-outlined text-base">delete</span>
                    <span className="hidden sm:inline">{deleting ? 'Deleting…' : 'Delete'}</span>
                  </button>
                </>
              )}
            </div>
          }
        />

        <div className="p-4 md:p-8">
          <div className="max-w-4xl mx-auto">
            {/* Summary Card */}
            <div className="bg-white rounded-xl border border-gray-200 mb-6">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-900">Estimate Summary</h3>
              </div>
              <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Estimate Number</p>
                  <p className="font-semibold text-gray-900">EST-{estimate.id.toString().padStart(5, '0')}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Created Date</p>
                  <p className="font-semibold text-gray-900">{formatDate(estimate.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Job</p>
                  <p className="font-semibold text-gray-900">{estimate.job_title || `Job #${estimate.job_id}`}</p>
                  {estimate.client_name && <p className="text-xs text-gray-400">{estimate.client_name}</p>}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Amount</p>
                  <p className="font-bold text-xl text-primary">
                    {formatCurrency(isEditing ? calculateTotal() : estimate.total_amount)}
                  </p>
                </div>
              </div>
              {isEditing && (
                <div className="p-6 border-t border-gray-200 grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={editStatus}
                      onChange={(e) => setEditStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                    >
                      <option value="draft">Draft</option>
                      <option value="sent">Sent</option>
                      <option value="approved">Approved</option>
                      <option value="declined">Declined</option>
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Items Table */}
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h3 className="text-lg font-bold text-gray-900">
                  Materials ({(isEditing ? editItems : estimate.items || []).filter(i => i.item_type !== 'heading').length} items)
                </h3>
                {isEditing && (
                  <button
                    onClick={addItem}
                    className="flex items-center gap-1 px-3 py-2 text-sm bg-primary text-white rounded-lg hover:opacity-90"
                  >
                    <span className="material-symbols-outlined text-base">add</span>
                    Add Item
                  </button>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs uppercase bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-6 py-3">Product</th>
                      <th className="px-6 py-3 text-right">Quantity</th>
                      <th className="px-6 py-3 text-right">Unit Price</th>
                      <th className="px-6 py-3 text-right">Total</th>
                      {isEditing && <th className="px-6 py-3 text-right">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {isEditing ? (
                      editItems.length > 0 ? (
                        editItems.map((item, index) => item.item_type === 'heading' ? (
                          // Section headings are first-class rows (#96) — editable
                          // name, no qty/price, removable
                          <tr key={index} className="bg-gray-50 border-b border-gray-200">
                            <td className="px-6 py-2.5" colSpan={4}>
                              <input
                                type="text"
                                value={item.product_name}
                                onChange={(e) => updateItem(index, 'product_name', e.target.value)}
                                className="w-full px-2 py-1 border border-gray-200 rounded bg-white font-bold text-gray-700 uppercase tracking-wide text-xs"
                                placeholder="Section heading"
                              />
                            </td>
                            <td className="px-6 py-2.5 text-right">
                              <button onClick={() => removeItem(index)} className="text-red-600 hover:text-red-800">
                                <span className="material-symbols-outlined text-base">delete</span>
                              </button>
                            </td>
                          </tr>
                        ) : (
                          <tr key={index} className="bg-white border-b border-gray-200">
                            <td className="px-6 py-4">
                              <input
                                type="text"
                                value={item.product_name}
                                onChange={(e) => updateItem(index, 'product_name', e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary"
                              />
                            </td>
                            <td className="px-6 py-4 text-right">
                              <input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                                className="w-20 px-2 py-1 border border-gray-300 rounded text-right focus:ring-2 focus:ring-primary focus:border-primary"
                              />
                            </td>
                            <td className="px-6 py-4 text-right">
                              <input
                                type="number"
                                step="0.01"
                                value={item.unit_price}
                                onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                                className="w-24 px-2 py-1 border border-gray-300 rounded text-right focus:ring-2 focus:ring-primary focus:border-primary"
                              />
                            </td>
                            <td className="px-6 py-4 text-right font-semibold">
                              {formatCurrency(item.total)}
                            </td>
                            <td className="px-6 py-4 text-right">
                              <button
                                onClick={() => removeItem(index)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <span className="material-symbols-outlined text-base">delete</span>
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="px-6 py-8 text-center text-gray-600">
                            No items. Click "Add Item" to add materials.
                          </td>
                        </tr>
                      )
                    ) : (
                      estimate.items && estimate.items.length > 0 ? (
                        estimate.items.map((item, index) => item.item_type === 'heading' ? (
                          <tr key={index} className="bg-gray-50 border-b border-gray-200">
                            <td className="px-6 py-2.5 font-bold text-gray-700 uppercase tracking-wide text-xs" colSpan={4}>
                              {item.product_name}
                            </td>
                          </tr>
                        ) : (
                          <tr key={index} className="bg-white border-b border-gray-200">
                            <td className="px-6 py-4 font-medium text-gray-900">
                              {item.product_name}
                            </td>
                            <td className="px-6 py-4 text-right">{item.quantity}</td>
                            <td className="px-6 py-4 text-right">{formatCurrency(item.unit_price)}</td>
                            <td className="px-6 py-4 text-right font-semibold">{formatCurrency(item.total)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} className="px-6 py-8 text-center text-gray-600">
                            No items in this estimate
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan={3} className="px-6 py-4 text-right font-bold text-gray-900">
                        Total
                      </td>
                      <td className="px-6 py-4 text-right font-bold text-xl text-primary">
                        {formatCurrency(isEditing ? calculateTotal() : estimate.total_amount)}
                      </td>
                      {isEditing && <td></td>}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};
